import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { BIEN_TYPE_LABELS, BienTypeSchema } from '../../lib/api-types';
import type { BienCreateInput } from '../../lib/api';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/Dialog';
import { FieldHelp } from '../../components/ui/FieldHelp';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Select } from '../../components/ui/Select';

const bienSchema = z.object({
  designation: z.string().min(1, 'La désignation est requise.'),
  type: BienTypeSchema,
  reference: z.string().optional(),
  superficie: z
    .string()
    .optional()
    .refine((v) => !v || /^\d+([.,]\d{1,4})?$/.test(v), {
      message: 'Superficie invalide (nombre positif attendu, ex. 300.5).',
    }),
  typeZone: z.string().optional(),
  affectation: z.string().optional(),
  titreJouissance: z.string().optional(),
  nomSurTitre: z.string().optional(),
});
type BienValues = z.infer<typeof bienSchema>;

/** Clearly fictitious demo values — sample labels only, never official data. */
const DEMO_BIEN: BienValues = {
  designation: 'Villa de démonstration (ex. : à modifier)',
  type: 'TERRAIN_BATI',
  reference: 'DERLIZ-DEMO-2026-001',
  superficie: '600',
  typeZone: 'Résidentielle',
  affectation: 'Habitation',
  titreJouissance: 'Titre foncier',
  nomSurTitre: 'M. Demo Exemple',
};

function opt(value: string | undefined): string | undefined {
  return value && value.trim().length > 0 ? value.trim() : undefined;
}

export function BienForm({
  onSubmit,
  onCancel,
  pending,
}: {
  onSubmit: (input: BienCreateInput) => void;
  onCancel: () => void;
  pending?: boolean;
}): JSX.Element {
  const {
    register,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<BienValues>({
    resolver: zodResolver(bienSchema),
    defaultValues: {
      designation: '',
      type: 'TERRAIN_BATI',
      reference: '',
      superficie: '',
      typeZone: '',
      affectation: '',
      titreJouissance: '',
      nomSurTitre: '',
    },
  });

  const [confirmDemo, setConfirmDemo] = useState(false);

  const applyDemo = () => {
    // Prefill coherent demo values client-side; the user can edit them after.
    (Object.keys(DEMO_BIEN) as (keyof BienValues)[]).forEach((key) => {
      setValue(key, DEMO_BIEN[key], { shouldValidate: false });
    });
  };

  const requestDemo = () => {
    const values = getValues();
    const hasData = (
      [
        'designation',
        'reference',
        'superficie',
        'typeZone',
        'affectation',
        'titreJouissance',
        'nomSurTitre',
      ] as const
    ).some((key) => (values[key] ?? '').trim().length > 0);
    if (hasData) {
      setConfirmDemo(true);
      return;
    }
    applyDemo();
  };

  const submit = handleSubmit((values) => {
    onSubmit({
      designation: values.designation.trim(),
      type: values.type,
      reference: opt(values.reference),
      // Decimal-as-string, normalised to a dot separator; the server validates.
      superficie: opt(values.superficie)?.replace(',', '.'),
      typeZone: opt(values.typeZone),
      affectation: opt(values.affectation),
      titreJouissance: opt(values.titreJouissance),
      nomSurTitre: opt(values.nomSurTitre),
    });
  });

  return (
    <form onSubmit={submit} className="space-y-4" noValidate>
      <div className="rounded-md border border-dashed border-brand-200 bg-brand-50/50 px-3 py-2">
        <Button type="button" size="sm" variant="outline" onClick={requestDemo} disabled={pending}>
          Utiliser un exemple de démonstration
        </Button>
        <FieldHelp className="mt-1">
          Remplit le formulaire avec des valeurs d’exemple, que vous pouvez ensuite modifier.
        </FieldHelp>
      </div>

      <div>
        <Label htmlFor="bien-designation">Désignation</Label>
        <Input
          id="bien-designation"
          placeholder="ex. : Villa R+1 — Ouaga 2000"
          aria-invalid={Boolean(errors.designation)}
          {...register('designation')}
        />
        {errors.designation ? (
          <p className="mt-1 text-sm text-red-600">{errors.designation.message}</p>
        ) : null}
        <FieldHelp>Le nom usuel du bien, tel qu’il apparaîtra dans les documents.</FieldHelp>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="bien-type">Type de bien</Label>
          <Select id="bien-type" {...register('type')}>
            {BienTypeSchema.options.map((t) => (
              <option key={t} value={t}>
                {BIEN_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
          <FieldHelp>La nature du bien évalué (ex. : terrain bâti à usage résidentiel).</FieldHelp>
        </div>
        <div>
          <Label htmlFor="bien-reference">Référence (facultatif)</Label>
          <Input
            id="bien-reference"
            placeholder="ex. : DERLIZ-DEMO-2026-001"
            {...register('reference')}
          />
          <FieldHelp example="TF 12345/DK">
            Référence cadastrale ou titre foncier, si disponible.
          </FieldHelp>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="bien-superficie">Superficie en m² (facultatif)</Label>
          <Input
            id="bien-superficie"
            inputMode="decimal"
            placeholder="ex. : 600"
            aria-invalid={Boolean(errors.superficie)}
            {...register('superficie')}
          />
          {errors.superficie ? (
            <p className="mt-1 text-sm text-red-600">{errors.superficie.message}</p>
          ) : null}
          <FieldHelp example="600 m²">Surface totale du bien, en mètres carrés.</FieldHelp>
        </div>
        <div>
          <Label htmlFor="bien-typeZone">Type de zone (facultatif)</Label>
          <Input id="bien-typeZone" placeholder="ex. : Résidentielle" {...register('typeZone')} />
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="bien-affectation">Affectation (facultatif)</Label>
          <Input
            id="bien-affectation"
            placeholder="ex. : Habitation"
            {...register('affectation')}
          />
        </div>
        <div>
          <Label htmlFor="bien-titreJouissance">Titre de jouissance (facultatif)</Label>
          <Input
            id="bien-titreJouissance"
            placeholder="ex. : Titre foncier"
            {...register('titreJouissance')}
          />
        </div>
        <div className="space-y-1">
          {/* LOT 9D — le PV imprime le titulaire DU TITRE, qui n’est pas le mandant : le mandant
              est très souvent une société tierce (design §11, « deux concepts distincts »). */}
          <Label htmlFor="bien-nomSurTitre">Nom sur le titre — titulaire (facultatif)</Label>
          <Input id="bien-nomSurTitre" {...register('nomSurTitre')} />
          <FieldHelp>
            Le titulaire inscrit sur le titre de propriété, s’il diffère du mandant.
          </FieldHelp>
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          Annuler
        </Button>
        <Button type="submit" loading={pending}>
          Créer le bien
        </Button>
      </div>

      <ConfirmDialog
        open={confirmDemo}
        onClose={() => setConfirmDemo(false)}
        onConfirm={() => {
          applyDemo();
          setConfirmDemo(false);
        }}
        title="Remplacer par l’exemple de démonstration ?"
        description="Des informations ont déjà été saisies. Voulez-vous les remplacer par les valeurs d’exemple ? Vous pourrez encore les modifier ensuite."
        confirmLabel="Remplacer"
      />
    </form>
  );
}
